import React, { useRef, useState } from "react";
import { Paperclip, X, FileText, AlertCircle, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { FullWidth } from "./BookingFormFields";

const MAX_FILES = 5;
const MAX_MB = 10;

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Multi-file uploader for booking forms. Each file uploads immediately to
 * /api/booking/attachments and stores its returned id in state, which is then
 * included in the booking submission's data.attachments array.
 */
export default function FileAttachmentField({
  label = "Attachments",
  hint = `Scripts, riders, set plots, contracts. PDF, Word, Excel, or images. Max ${MAX_FILES} files, ${MAX_MB} MB each.`,
  value = [],
  onChange,
  testId = "attachments",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const onSelect = async (e) => {
    setError("");
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (value.length + files.length > MAX_FILES) {
      setError(`You can attach at most ${MAX_FILES} files.`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of files) {
        if (file.size > MAX_MB * 1024 * 1024) {
          setError(`"${file.name}" exceeds the ${MAX_MB} MB limit.`);
          continue;
        }
        const form = new FormData();
        form.append("file", file);
        const r = await api.post("/booking/attachments", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(r.data);
      }
      onChange([...value, ...uploaded]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed. Try a smaller file or a different format.";
      setError(detail);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <FullWidth>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
        <p className="font-overline text-[0.65rem] text-stone-400 tracking-[0.18em]">
          {label}
        </p>
        <span className="font-overline text-[0.6rem] text-stone-500">
          {value.length} / {MAX_FILES}
        </span>
      </div>
      <p className="font-body text-[0.78rem] text-stone-500 mb-4 leading-relaxed">{hint}</p>

      <div className="space-y-3" data-testid={testId}>
        {value.map((f, i) => (
          <div
            key={f.id}
            data-testid={`${testId}-item-${i}`}
            className="flex items-center gap-3 border border-white/10 rounded-xl px-4 py-3 bg-stone-900/40"
          >
            <FileText size={16} className="text-[#DE9E48] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-body text-sm text-stone-100 truncate">{f.filename}</p>
              <p className="font-overline text-[0.6rem] text-stone-500 tracking-[0.12em]">
                {fmtSize(f.size)}
              </p>
            </div>
            <button
              type="button"
              data-testid={`${testId}-remove-${i}`}
              onClick={() => removeAt(i)}
              aria-label={`Remove ${f.filename}`}
              className="w-7 h-7 rounded-full flex items-center justify-center text-stone-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        ))}

        {value.length < MAX_FILES && (
          <button
            type="button"
            data-testid={`${testId}-add`}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="w-full border border-dashed border-white/15 rounded-xl py-4 font-overline text-xs text-stone-300 hover:border-[#DE9E48] hover:text-[#DE9E48] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <Paperclip size={14} /> Click to add files
              </>
            )}
          </button>
        )}

        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={onSelect}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/plain,text/csv,image/jpeg,image/png,image/webp,image/heic,image/heif"
          className="hidden"
          data-testid={`${testId}-input`}
        />

        {error && (
          <p
            data-testid={`${testId}-error`}
            className="font-overline text-[0.65rem] text-rose-400 inline-flex items-center gap-2"
          >
            <AlertCircle size={12} /> {error}
          </p>
        )}
      </div>
    </FullWidth>
  );
}
